import { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { BarChart3, X, ChevronDown, Upload, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import WeightedPricesChart from '@/components/WeightedPricesChart';
import ExternalAnalyticsTab from '@/components/ExternalAnalyticsTab';
import MetricsTab from '@/components/MetricsTab';
import { toast } from 'sonner';
import { formatAxiosError } from '@/lib/api';

const EXTERNAL_OWNER_EMAIL = 'supply@chemdel.net';

/**
 * Отдельная страница «Аналитика средневзвешенных цен» для организаторов.
 * Содержит только фильтры и график. Не дублирует архив тендеров.
 */
export default function AnalyticsPage() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [productName, setProductName] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [productOpen, setProductOpen] = useState(false);
  const productBoxRef = useRef(null);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const handleExportXlsx = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('bid_end_from', dateFrom);
      if (dateTo) params.set('bid_end_to', dateTo);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/archive/export-tenders-xlsx?${params.toString()}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        let detail = '';
        try { detail = (await res.json()).detail || ''; } catch { /* ignore */ }
        toast.error(detail || `Не удалось выгрузить отчёт (HTTP ${res.status})`);
        return;
      }
      const blob = await res.blob();
      const disposition = res.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `basequote_tenders_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success('Отчёт выгружен');
    } catch (err) {
      toast.error(formatAxiosError(err));
    } finally {
      setExporting(false);
    }
  };

  const [bySupplier, setBySupplier] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierCompany, setSupplierCompany] = useState('');
  const [direction, setDirection] = useState(''); // '' | 'purchase' | 'logistics'

  // Валюта аналитики
  const [currencies, setCurrencies] = useState([]);   // ['USD','RUB','EUR'...]
  const [currencyMode, setCurrencyMode] = useState('native'); // native|filter|convert
  const [currency, setCurrency] = useState('');

  // Загрузка справочников
  useEffect(() => {
    (async () => {
      try {
        const [{ data: prods }, { data: sups }, { data: ccys }] = await Promise.all([
          api.get('/products').catch(() => ({ data: [] })),
          api.get('/admin/archive/analytics/suppliers').catch(() => ({ data: [] })),
          api.get('/admin/archive/analytics/currencies').catch(() => ({ data: [] })),
        ]);
        const prodNames = Array.isArray(prods)
          ? prods.map(p => p?.name || p).filter(Boolean)
          : [];
        setProducts(prodNames);
        setSuppliers(Array.isArray(sups) ? sups : []);
        setCurrencies(Array.isArray(ccys) ? ccys : []);
      } catch {/* ignore */}
    })();
  }, []);

  // Закрывать дропдаун товара по клику снаружи
  useEffect(() => {
    const handler = (e) => {
      if (productBoxRef.current && !productBoxRef.current.contains(e.target)) {
        setProductOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredProducts = useMemo(() => {
    if (!productQuery) return products;
    const q = productQuery.toLowerCase();
    return products.filter(p => (p || '').toLowerCase().includes(q));
  }, [products, productQuery]);

  // Сбрасываем выбранного поставщика при отключении режима
  useEffect(() => {
    if (!bySupplier) setSupplierCompany('');
  }, [bySupplier]);

  // Доступ — только организаторам (проверка после всех хуков, чтобы не нарушать
  // правило одинакового порядка вызовов React-хуков между рендерами).
  if (user && user.role !== 'organizer') {
    return <Navigate to="/dashboard" replace />;
  }

  const resetFilters = () => {
    setProductName('');
    setProductQuery('');
    setDateFrom('');
    setDateTo('');
    setBySupplier(false);
    setSupplierCompany('');
    setCurrencyMode('native');
    setCurrency('');
  };

  const isExternalOwner = (user?.email || '').toLowerCase() === EXTERNAL_OWNER_EMAIL;
  const isSuperAdmin = !!user?.is_super_admin;
  const isPurchaseOrg = (user?.tender_direction || '').toLowerCase() === 'purchase';
  const canSeeMetrics = isPurchaseOrg || isSuperAdmin;

  // Карточка «Выгрузка результатов торгов в Excel».
  const exportCard = (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="tenders-export-card">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-700" />
            <h2 className="text-base font-heading font-bold text-slate-900">
              Выгрузка результатов торгов (Excel)
            </h2>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            По каждому завершённому тендеру: шапка (продукт, дата, ценовой ориентир, шаг),
            результат победителя и отклонение от потолка, а также все ставки каждой компании
            в хронологическом порядке. Использует те же фильтры по датам, что и график ниже.
          </p>
        </div>
        <Button onClick={handleExportXlsx}
          disabled={exporting}
          data-testid="tenders-export-xlsx-btn"
          className="h-9 px-4 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium">
          {exporting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Формирую…</>
          ) : (
            <><FileSpreadsheet className="w-4 h-4 mr-2" />Выгрузить в Excel</>
          )}
        </Button>
      </div>
    </div>
  );

  // Карточка «Аналитика по тендерам платформы» — оригинал без изменений,
  // только вынесена в переменную чтобы использовать как контент таба.
  const platformAnalyticsCard = (
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-700" />
            <h1 className="text-base font-heading font-bold text-slate-900">
              Аналитика средневзвешенных цен
            </h1>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Средневзвешенные по объёму цены по неделям для завершённых тендеров.
            В режиме «по поставщику» — динамика конкретного поставщика, независимо от результата.
          </p>
        </div>

        <div className="px-6 py-4 border-b border-slate-200">
          <div className="grid md:grid-cols-4 gap-3 items-end">
            <div className="space-y-1" ref={productBoxRef}>
              <label className="text-xs text-slate-600">Закупаемый товар</label>
              <div className="relative">
                <div className="flex items-center h-8 rounded-sm border border-slate-300 bg-white px-2 cursor-text"
                  onClick={() => setProductOpen(true)}>
                  <Input value={productOpen ? productQuery : (productName || '')}
                    onChange={(e) => { setProductQuery(e.target.value); setProductOpen(true); }}
                    onFocus={() => { setProductOpen(true); setProductQuery(''); }}
                    placeholder="Все товары"
                    className="h-7 text-xs border-0 p-0 focus-visible:ring-0 shadow-none"
                    data-testid="analytics-product-name" />
                  {productName && !productOpen && (
                    <button type="button"
                      onClick={(e) => { e.stopPropagation(); setProductName(''); setProductQuery(''); }}
                      className="text-slate-400 hover:text-slate-700 mr-1"
                      data-testid="analytics-product-clear"
                      title="Очистить">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </div>
                {productOpen && (
                  <div className="absolute z-10 mt-1 w-full max-h-56 overflow-auto bg-white border border-slate-200 rounded-sm shadow-md">
                    {filteredProducts.length === 0 ? (
                      <div className="px-2 py-1.5 text-xs text-slate-400">Нет совпадений</div>
                    ) : filteredProducts.map(p => (
                      <button key={p} type="button"
                        onClick={() => { setProductName(p); setProductOpen(false); setProductQuery(''); }}
                        data-testid={`analytics-product-option-${p}`}
                        className="block w-full text-left px-2 py-1.5 text-xs hover:bg-slate-100">
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-slate-600">Конец торгов с</label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="h-8 text-xs rounded-sm border-slate-300"
                data-testid="analytics-date-from" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-slate-600">Конец торгов по</label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="h-8 text-xs rounded-sm border-slate-300"
                data-testid="analytics-date-to" />
            </div>
            <div className="flex items-center gap-3 pb-1">
              <Button variant="ghost" size="sm" onClick={resetFilters}
                data-testid="analytics-reset"
                className="h-7 text-[11px] text-slate-500 hover:text-slate-700 ml-auto">
                Сбросить фильтры
              </Button>
            </div>
          </div>

          {/* Чекбокс «Только по поставщику» */}
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <label className="text-[12px] text-slate-700 flex items-center gap-2 cursor-pointer select-none"
              data-testid="analytics-by-supplier-label">
              <Checkbox checked={bySupplier} onCheckedChange={setBySupplier}
                data-testid="analytics-by-supplier" />
              <span className="font-medium">Только по поставщику</span>
            </label>
            {bySupplier && (
              <div className="flex items-center gap-2">
                <Select value={supplierCompany} onValueChange={setSupplierCompany}>
                  <SelectTrigger className="h-8 w-72 text-xs rounded-sm border-slate-300"
                    data-testid="analytics-supplier">
                    <SelectValue placeholder="Выберите поставщика…" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.length === 0 ? (
                      <div className="px-2 py-1.5 text-xs text-slate-400">Нет поставщиков</div>
                    ) : suppliers.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {supplierCompany && (
                  <button type="button" onClick={() => setSupplierCompany('')}
                    className="text-slate-400 hover:text-slate-700"
                    data-testid="analytics-supplier-clear"
                    title="Сбросить">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Направление тендера */}
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <span className="text-[12px] text-slate-700 font-medium">Направление:</span>
            <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden text-[11px]">
              <button type="button" onClick={() => setDirection('')}
                data-testid="analytics-dir-all"
                className={`px-2.5 py-1 ${direction === '' ? 'bg-slate-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>
                Все
              </button>
              <button type="button" onClick={() => setDirection('purchase')}
                data-testid="analytics-dir-purchase"
                className={`px-2.5 py-1 border-l border-slate-300 ${direction === 'purchase' ? 'bg-blue-700 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>
                Закупки
              </button>
              <button type="button" onClick={() => setDirection('logistics')}
                data-testid="analytics-dir-logistics"
                className={`px-2.5 py-1 border-l border-slate-300 ${direction === 'logistics' ? 'bg-amber-700 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>
                Логистика
              </button>
            </div>
          </div>

          {/* Валюта аналитики */}
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <span className="text-[12px] text-slate-700 font-medium">Валюта:</span>
            <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden text-[11px]">
              <button type="button" onClick={() => { setCurrencyMode('native'); setCurrency(''); }}
                data-testid="analytics-ccy-mode-native"
                className={`px-2.5 py-1 ${currencyMode === 'native' ? 'bg-slate-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>
                Все «как есть»
              </button>
              <button type="button" onClick={() => setCurrencyMode('filter')}
                data-testid="analytics-ccy-mode-filter"
                className={`px-2.5 py-1 border-l border-slate-300 ${currencyMode === 'filter' ? 'bg-slate-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>
                Только в выбранной
              </button>
              <button type="button" onClick={() => setCurrencyMode('convert')}
                data-testid="analytics-ccy-mode-convert"
                className={`px-2.5 py-1 border-l border-slate-300 ${currencyMode === 'convert' ? 'bg-slate-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>
                Привести к одной (кросс-курс ЦБ)
              </button>
            </div>
            {currencyMode !== 'native' && (
              <div className="flex items-center gap-2">
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger className="h-8 w-32 text-xs rounded-sm border-slate-300"
                    data-testid="analytics-currency">
                    <SelectValue placeholder="Валюта…" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.length === 0 ? (
                      <div className="px-2 py-1.5 text-xs text-slate-400">Нет валют</div>
                    ) : currencies.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {currency && (
                  <button type="button" onClick={() => setCurrency('')}
                    className="text-slate-400 hover:text-slate-700"
                    data-testid="analytics-currency-clear"
                    title="Сбросить">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
            {currencyMode === 'convert' && (
              <span className="text-[10px] text-slate-500 italic">
                Кросс-курсы ЦБ РФ берутся автоматически на дату окончания торгов каждого тендера.
              </span>
            )}
          </div>
        </div>

        <div className="px-6 py-4">
          {bySupplier && !supplierCompany ? (
            <div className="py-6 px-4 bg-amber-50 border border-amber-200 text-xs text-amber-800 rounded-sm">
              Выберите поставщика из списка, чтобы построить график по нему.
            </div>
          ) : (
            <WeightedPricesChart
              productName={productName}
              bidEndFrom={dateFrom}
              bidEndTo={dateTo}
              supplierCompany={bySupplier ? supplierCompany : ''}
              currencyMode={currencyMode}
              currency={currency}
              direction={direction}
            />
          )}
        </div>
      </div>
  );

  // Без вкладки внешней аналитики и без права на метрики — рендерим как раньше.
  if (!isExternalOwner && !canSeeMetrics) {
    return (
      <div className="space-y-4" data-testid="analytics-page">
        {exportCard}
        {platformAnalyticsCard}
      </div>
    );
  }

  // Собираем список вкладок динамически.
  const tabs = [
    { key: 'platform', label: 'Тендеры платформы', testid: 'tab-platform-analytics' },
  ];
  if (isExternalOwner) tabs.push({ key: 'external', label: 'Анализ файлов', testid: 'tab-external-analytics' });
  if (canSeeMetrics)   tabs.push({ key: 'metrics',  label: 'Метрики',       testid: 'tab-metrics-analytics'  });

  return (
    <div className="space-y-4" data-testid="analytics-page">
      <Tabs defaultValue="platform" className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-sm">
          {tabs.map(t => (
            <TabsTrigger key={t.key} value={t.key} data-testid={t.testid} className="text-xs">
              <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="platform" className="mt-3 space-y-4">
          {exportCard}
          {platformAnalyticsCard}
        </TabsContent>
        {isExternalOwner && (
          <TabsContent value="external" className="mt-3">
            <ExternalAnalyticsTab />
          </TabsContent>
        )}
        {canSeeMetrics && (
          <TabsContent value="metrics" className="mt-3">
            <MetricsTab isSuperAdmin={isSuperAdmin} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
